// ActivityFeed Component - Displays community activities

import React from 'react';
import {
  Star,
  Heart,
  MapPin,
  Camera,
  UsersRound,
  MessageSquare,
  Calendar,
  Share2,
  User,
} from 'lucide-react';
import type { Activity, ActivityType } from '../../types/activity';
import { Timestamp } from '@firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface ActivityFeedProps {
  activities: Activity[];
  loading: boolean;
  onViewProfile: (userId: string) => void;
  onViewPlace: (placeId: string) => void;
  onViewGroup: (groupId: string) => void;
}

const getActivityIcon = (type: ActivityType) => {
  switch (type) {
    case 'review':
      return { icon: Star, color: 'text-yellow-500 bg-yellow-100' };
    case 'favorite':
      return { icon: Heart, color: 'text-red-500 bg-red-100' };
    case 'checkin':
      return { icon: MapPin, color: 'text-green-500 bg-green-100' };
    case 'photo':
      return { icon: Camera, color: 'text-blue-500 bg-blue-100' };
    case 'group_join':
      return { icon: UsersRound, color: 'text-purple-500 bg-purple-100' };
    case 'group_post':
      return { icon: MessageSquare, color: 'text-indigo-500 bg-indigo-100' };
    case 'event_attend':
      return { icon: Calendar, color: 'text-orange-500 bg-orange-100' };
    case 'place_share':
      return { icon: Share2, color: 'text-teal-500 bg-teal-100' };
    default:
      return { icon: Star, color: 'text-gray-500 bg-gray-100' };
  }
};

const getActivityText = (activity: Activity): string => {
  switch (activity.type) {
    case 'review':
      return `hat ${activity.placeName} bewertet`;
    case 'favorite':
      return `hat ${activity.placeName} zu den Favoriten hinzugefuegt`;
    case 'checkin':
      return `war bei ${activity.placeName}`;
    case 'photo':
      return `hat ein Foto bei ${activity.placeName} geteilt`;
    case 'group_join':
      return `ist der Gruppe "${activity.groupName}" beigetreten`;
    case 'group_post':
      return `hat in "${activity.groupName}" gepostet`;
    case 'event_attend':
      return `nimmt an "${activity.eventTitle}" teil`;
    case 'place_share':
      return `hat ${activity.placeName} empfohlen`;
    default:
      return 'war aktiv';
  }
};

export function ActivityFeed({
  activities,
  loading,
  onViewProfile,
  onViewPlace,
  onViewGroup,
}: ActivityFeedProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <Star className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Noch keine Aktivitaeten
        </h3>
        <p className="text-gray-500">
          Hier erscheinen Aktivitaeten von Community-Mitgliedern.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => (
        <ActivityCard
          key={activity.id}
          activity={activity}
          onViewProfile={onViewProfile}
          onViewPlace={onViewPlace}
          onViewGroup={onViewGroup}
        />
      ))}
    </div>
  );
}

// Activity Card Component
interface ActivityCardProps {
  activity: Activity;
  onViewProfile: (userId: string) => void;
  onViewPlace: (placeId: string) => void;
  onViewGroup: (groupId: string) => void;
}

function ActivityCard({
  activity,
  onViewProfile,
  onViewPlace,
  onViewGroup,
}: ActivityCardProps) {
  const { icon: Icon, color } = getActivityIcon(activity.type);
  const activityText = getActivityText(activity);

  const formatTime = (date: Timestamp | Date) => {
    const d = date instanceof Timestamp ? date.toDate() : date;
    return formatDistanceToNow(d, { locale: de, addSuffix: true });
  };

  const handlePlaceClick = () => {
    if (activity.placeId) {
      onViewPlace(activity.placeId);
    }
  };

  const handleGroupClick = () => {
    if (activity.groupId) {
      onViewGroup(activity.groupId);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start gap-3">
        {/* User Avatar */}
        <button
          onClick={() => onViewProfile(activity.userId)}
          className="flex-shrink-0"
        >
          {activity.userPhotoUrl ? (
            <img
              src={activity.userPhotoUrl}
              alt={activity.userName}
              className="w-10 h-10 rounded-full object-cover hover:opacity-80 transition-opacity"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
              <User className="w-5 h-5 text-gray-400" />
            </div>
          )}
        </button>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <button
                onClick={() => onViewProfile(activity.userId)}
                className="font-medium text-gray-900 hover:text-indigo-600 transition-colors"
              >
                {activity.userName}
              </button>
              <span className="text-gray-600 ml-1">{activityText}</span>
            </div>
            <div className={`flex-shrink-0 p-1.5 rounded-lg ${color}`}>
              <Icon className="w-4 h-4" />
            </div>
          </div>

          {/* Timestamp */}
          <p className="text-sm text-gray-500 mt-0.5">
            {formatTime(activity.createdAt)}
          </p>

          {/* Content (if any) */}
          {activity.content && (
            <p className="text-gray-700 mt-2 text-sm line-clamp-2">
              "{activity.content}"
            </p>
          )}

          {/* Rating (for reviews) */}
          {activity.type === 'review' && activity.rating && (
            <div className="flex items-center gap-0.5 mt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < activity.rating!
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-200'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Place Preview */}
          {activity.placeId && activity.placeImageUrl && (
            <button
              onClick={handlePlaceClick}
              className="mt-3 w-full flex items-center gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <img
                src={activity.placeImageUrl}
                alt={activity.placeName}
                className="w-16 h-12 rounded-lg object-cover"
              />
              <div className="flex-1 text-left">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-indigo-500" />
                  {activity.placeName}
                </span>
              </div>
            </button>
          )}

          {/* Group Preview */}
          {activity.groupId && !activity.placeId && (
            <button
              onClick={handleGroupClick}
              className="mt-3 flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm"
            >
              <UsersRound className="w-4 h-4" />
              {activity.groupName}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityFeed;
